/**
 * Concurrency gate (semaphore) used to cap in-flight HTTP requests per browser
 * tab (ARCHITECTURE.md R37 — Phase 1 safe refactor).
 *
 * Why this exists:
 * - Each tenant request holds one DB connection for its whole lifetime. The
 *   tenant pool (pool_size 8 + overflow 12 = 20) is shared by every open tab.
 * - Over HTTP/2 the browser no longer caps concurrency at ~6/origin, so a
 *   dashboard mount or a multi-window setup can fire 15–25 requests at once and
 *   exhaust the pool (`QueuePool limit … timed out`).
 *
 * Robustness guarantees (so the gate can never break login or freeze the UI):
 * - **Login-safe exemptions** via `isGateExemptRequest` — `/status`, `/token`,
 *   `/users/me` never wait on the gate (see tests).
 * - **Soft cap with a max wait.** A queued request waits at most `maxWaitMs` for
 *   a slot; if none frees in time it proceeds anyway (overcommit).
 * - **Balanced accounting.** Every `acquire()` increments `active` exactly once
 *   and the matching `release()` decrements it exactly once.
 */

const DEFAULT_MAX_WAIT_MS = 20_000;

/** Relative API paths that must NEVER wait on the concurrency gate. */
export const GATE_EXEMPT_PATH_SUFFIXES = ['/status', '/token', '/users/me'];

/**
 * True when this axios config must bypass the gate (login, health, opt-out).
 * @param {import('axios').InternalAxiosRequestConfig} [config]
 */
export function isGateExemptRequest(config) {
  if (config?.skipConcurrencyGate === true) return true;
  const url = typeof config?.url === 'string' ? config.url : '';
  if (!url) return false;
  return GATE_EXEMPT_PATH_SUFFIXES.some(
    (suffix) => url === suffix || url.endsWith(suffix),
  );
}

/**
 * Create a concurrency gate.
 * @param {number} maxConcurrent Maximum number of slots handed out at once.
 * @param {{ maxWaitMs?: number }} [options]
 * @returns {{ acquire: () => Promise<void>, release: () => void, getStats: () => {active:number, queued:number, max:number} }}
 */
export function createConcurrencyGate(maxConcurrent, options = {}) {
  const max = Number.isFinite(maxConcurrent) && maxConcurrent > 0 ? Math.floor(maxConcurrent) : 1;
  const maxWaitMs = Number.isFinite(options.maxWaitMs) && options.maxWaitMs >= 0
    ? options.maxWaitMs
    : DEFAULT_MAX_WAIT_MS;

  let active = 0;
  const waiters = [];

  function wake() {
    if (active >= max) return;
    const entry = waiters.shift();
    if (!entry) return;
    if (entry.timer) clearTimeout(entry.timer);
    active += 1;
    entry.resolve();
  }

  function acquire() {
    if (active < max) {
      active += 1;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const entry = { resolve, timer: null };
      if (maxWaitMs > 0) {
        entry.timer = setTimeout(() => {
          const idx = waiters.indexOf(entry);
          if (idx === -1) return;
          waiters.splice(idx, 1);
          active += 1;
          resolve();
        }, maxWaitMs);
      }
      waiters.push(entry);
    });
  }

  function release() {
    active = Math.max(0, active - 1);
    wake();
  }

  function getStats() {
    return { active, queued: waiters.length, max };
  }

  return { acquire, release, getStats };
}

export default createConcurrencyGate;
